import { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import type { Ticket, TicketStatus, TicketQueue } from '@runninglime/shared';
import { TICKET_STATUS_LABELS, TICKET_QUEUE_LABELS } from '@runninglime/shared';
import { apiGet, apiPost, apiPut } from '../../lib/api.ts';
import { formatDate } from '../../lib/utils.ts';
import { StatusBadge, PriorityBadge, QueueBadge, LoadingSpinner } from '../../components/ui/Shared.tsx';

const STATUSES: TicketStatus[] = ['open', 'in-progress-l1', 'in-progress-l2', 'resolved', 'closed'];
const QUEUES: TicketQueue[] = ['general', 'billing', 'shipping', 'product-issue', 'returns', 'technical'];

interface StaffMember {
  id: string;
  contactName: string;
  role: string;
}

export default function AdminTicketDetail() {
  const { id } = useParams<{ id: string }>();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [assigning, setAssigning] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prevMsgCount = useRef<number | null>(null);

  const load = async () => {
    const res = await apiGet<Ticket>(`/admin/tickets/${id}`);
    if (res.success && res.data) setTicket(res.data);
    setLoading(false);
  };

  const loadStaff = async () => {
    const res = await apiGet<StaffMember[]>('/admin/tickets/staff/assignable');
    if (res.success && res.data) setStaff(res.data);
  };

  useEffect(() => { load(); loadStaff(); }, [id]);
  useEffect(() => {
    if (!ticket) return;
    const count = ticket.messages.length;
    // Only auto-scroll when a new message is added, not on initial load
    if (prevMsgCount.current !== null && count > prevMsgCount.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevMsgCount.current = count;
  }, [ticket?.messages.length]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    const res = await apiPost(`/admin/tickets/${id}/messages`, {
      text: message.trim(),
      internal: isInternal,
    });
    if (res.success) {
      setMessage('');
      setIsInternal(false);
      await load();
    }
    setSending(false);
  };

  const handleStatusChange = async (status: TicketStatus) => {
    setUpdatingStatus(true);
    await apiPut(`/admin/tickets/${id}/status`, { status });
    await load();
    setUpdatingStatus(false);
  };

  const handleQueueChange = async (queue: TicketQueue) => {
    await apiPut(`/admin/tickets/${id}/status`, { queue });
    await load();
  };

  const handleAssign = async (userId: string | null) => {
    setAssigning(true);
    await apiPut(`/admin/tickets/${id}/assign`, { assignToUserId: userId });
    await load();
    setAssigning(false);
  };

  if (loading) return <LoadingSpinner />;
  if (!ticket) return <div className="text-center py-8 text-gray-500">Ticket not found</div>;

  return (
    <div className="animate-fade-in">
      <Link to="/admin/tickets" className="text-sm text-gray-500 hover:text-green transition mb-4 inline-flex items-center gap-1">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Tickets
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chat Column */}
        <div className="lg:col-span-2">
          {/* Header */}
          <div className="glass rounded-2xl p-5 mb-4">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <StatusBadge status={ticket.status} />
              <PriorityBadge priority={ticket.priority} />
              <QueueBadge queue={ticket.queue} />
              {ticket.orderId && (
                <span className="text-xs text-gray-400">Order #{ticket.orderId.slice(0, 8)}</span>
              )}
            </div>
            <h1 className="text-xl font-bold text-dark">{ticket.subject}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {ticket.companyName} · Created {formatDate(ticket.createdAt)}
            </p>
            {ticket.assignment && (
              <p className="text-xs text-gray-400 mt-1">
                Assigned to <span className="font-medium text-dark">{ticket.assignment.assignedToName}</span>
                {' '}by {ticket.assignment.assignedByName}
              </p>
            )}
          </div>

          {/* Messages */}
          <div className="space-y-3 mb-4 max-h-[500px] overflow-y-auto pr-1">
            {ticket.messages.map((msg) => {
              const isStaff = msg.from === 'admin' || msg.from === 'employee';
              return (
                <div key={msg.id} className={`flex ${isStaff ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-5 py-3 ${
                    msg.internal
                      ? 'bg-yellow-50 border-2 border-dashed border-yellow-300 text-dark rounded-br-md'
                      : isStaff
                        ? 'bg-gradient-to-br from-lime to-green text-dark rounded-br-md'
                        : 'bg-white shadow-sm rounded-bl-md'
                  }`}>
                    <div className={`text-xs mb-1 font-medium ${
                      msg.internal ? 'text-yellow-700' : isStaff ? 'text-dark/70' : 'text-gray-400'
                    }`}>
                      {msg.senderName}
                      {msg.from === 'employee' ? ' (Employee)' : msg.from === 'admin' ? ' (Admin)' : ' (Customer)'}
                      {msg.internal && ' · 🔒 Internal'}
                      {' · '}{formatDate(msg.timestamp)}
                    </div>
                    <p className={`text-sm leading-relaxed whitespace-pre-wrap ${
                      msg.internal ? 'text-yellow-900' : isStaff ? 'text-dark' : 'text-gray-700'
                    }`}>
                      {msg.text}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Reply */}
          <form onSubmit={handleSend} className="glass rounded-2xl p-4">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={isInternal ? 'Write an internal note (not visible to customer)...' : 'Reply to customer...'}
              rows={3}
              className={`w-full px-4 py-2.5 rounded-xl border outline-none transition resize-none mb-3 ${
                isInternal
                  ? 'border-yellow-300 bg-yellow-50/50 focus:ring-2 focus:ring-yellow-300/50 focus:border-yellow-400'
                  : 'border-gray-200 focus:ring-2 focus:ring-lime/50 focus:border-lime'
              }`}
            />
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={isInternal}
                  onChange={(e) => setIsInternal(e.target.checked)}
                  className="rounded border-gray-300 text-yellow-500 focus:ring-yellow-400"
                />
                <span className="text-xs font-medium text-gray-600">
                  🔒 Internal note <span className="text-gray-400">(staff only)</span>
                </span>
              </label>
              <button
                type="submit"
                disabled={sending || !message.trim()}
                className={`px-6 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50 transition ${
                  isInternal
                    ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                    : 'btn-gradient'
                }`}
              >
                {sending ? 'Sending...' : isInternal ? 'Add Note' : 'Send Reply'}
              </button>
            </div>
          </form>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          {/* Status */}
          <div className="glass rounded-2xl p-5">
            <h3 className="font-semibold text-dark mb-3">Status</h3>
            <div className="grid grid-cols-1 gap-2">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => handleStatusChange(s)}
                  disabled={updatingStatus || s === ticket.status}
                  className={`px-3 py-2 rounded-xl text-xs font-medium transition ${
                    s === ticket.status
                      ? 'bg-lime text-dark ring-2 ring-lime'
                      : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                  } disabled:opacity-50`}
                >
                  {TICKET_STATUS_LABELS[s]}
                </button>
              ))}
            </div>
          </div>

          {/* Queue */}
          <div className="glass rounded-2xl p-5">
            <h3 className="font-semibold text-dark mb-3">Queue</h3>
            <select
              value={ticket.queue}
              onChange={(e) => handleQueueChange(e.target.value as TicketQueue)}
              className="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 focus:ring-2 focus:ring-lime/50 focus:border-lime outline-none"
            >
              {QUEUES.map((q) => (
                <option key={q} value={q}>{TICKET_QUEUE_LABELS[q]}</option>
              ))}
            </select>
          </div>

          {/* Assignment */}
          <div className="glass rounded-2xl p-5">
            <h3 className="font-semibold text-dark mb-3">Assignment</h3>
            {ticket.assignment ? (
              <div className="space-y-2">
                <div className="bg-lime/10 rounded-xl px-3 py-2 text-sm">
                  <p className="font-medium text-dark">{ticket.assignment.assignedToName}</p>
                  <p className="text-xs text-gray-500">by {ticket.assignment.assignedByName}</p>
                </div>
                <button
                  onClick={() => handleAssign(null)}
                  disabled={assigning}
                  className="w-full px-3 py-2 rounded-xl text-xs font-medium border border-red-200 text-red-600 hover:bg-red-50 transition disabled:opacity-50"
                >
                  {assigning ? 'Updating...' : 'Unassign'}
                </button>
              </div>
            ) : (
              <p className="text-xs text-gray-400 mb-2">Not assigned</p>
            )}
            <select
              value=""
              onChange={(e) => e.target.value && handleAssign(e.target.value)}
              disabled={assigning}
              className="w-full mt-2 px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 disabled:opacity-50"
            >
              <option value="">Assign to...</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.contactName} ({s.role})
                </option>
              ))}
            </select>
          </div>

          {/* Ticket Info */}
          <div className="glass rounded-2xl p-5">
            <h3 className="font-semibold text-dark mb-3">Ticket Info</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Ticket ID</dt>
                <dd className="font-mono text-dark text-xs">{ticket.id.slice(0, 12)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Company</dt>
                <dd className="text-dark text-xs">{ticket.companyName}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Customer</dt>
                <dd className="font-mono text-dark text-xs">{ticket.userId.slice(0, 12)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Priority</dt>
                <dd><PriorityBadge priority={ticket.priority} /></dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Messages</dt>
                <dd className="text-dark">{ticket.messages.length}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Created</dt>
                <dd className="text-dark text-xs">{formatDate(ticket.createdAt)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Updated</dt>
                <dd className="text-dark text-xs">{formatDate(ticket.updatedAt)}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
